// ─────────────────────────────────────────────────────────────────────────
// The active brand, resolved at build time.
//
// `src/generated/brand.js` is a verbatim copy of brand/$BRAND/brand.config.js
// (see scripts/apply-brand.mjs). Everything the editor needs to look like a
// given brand comes through here or through src/js/palette.js (which re-exports
// this module's P, so every existing `P.<name>` call site is brand-aware).
// ─────────────────────────────────────────────────────────────────────────
export { BRAND, P, SERIES, TYPE, RULES, CHROME, FONTS, fonts, PPTX, icons, FURNITURE, COVER, SPLIT, IMPORT, INFOGRAPHIC, GROUNDS, favicon, DIAGRAMS, DOCX } from "../generated/brand";
// The front-page artwork the build inlined (empty for a brand without one).
export { coverImage } from "../generated/cover";

import { P, CHROME, FONTS, TYPE, RULES, FURNITURE, favicon, exportFontCss as brandExportFontCss } from "../generated/brand";

// ── text-colour guard ───────────────────────────────────────────────────────
// A brand may forbid some of its own colours as a text colour — Telia's
// guidelines are explicit that Core Purple is never one. Blocks that tint
// small type with an element's accent (a card's tag, a quote's attribution, a
// stat's caption) run the accent through this first, so an accent that is
// legitimate as a graphic cannot become text through the back door.
const NEVER_TEXT = (RULES.neverText || []).map((r) => r.color);

export function textSafe(color, fallback) {
  const c = rgbOf(color);
  const hit = c && NEVER_TEXT.some((n) => rgbOf(n) === c);
  return hit ? (fallback || P.light) : color;
}

// ── micro-typography ────────────────────────────────────────────────────────
// Kickers, card tags, container labels, chart legends, stat captions — the
// small type that carries a deck's structure. It is the part of a deck most
// likely to break a brand's typographic rules, so the brand states it once
// (TYPE.microFont / microCase / microTracking) and every block, the exported
// player and the converter read it from here.
export const MICRO = {
  font: FONTS[TYPE.microFont] || FONTS.mono,
  transform: TYPE.microCase,     // a CSS text-transform value
  tracking: TYPE.microTracking,  // default kicker letter-spacing, px
  // All-caps micro-type needs tracking to stay legible; sentence case does
  // not, and Telia's guidelines rule out the caps in the first place. Wrap a
  // hand-authored tracking value in this and it survives only where it helps.
  track: (px) => (TYPE.microCase === "uppercase" ? px : 0),
  // Apply the brand's case to a string, for the places that transform text
  // rather than style it (the canvas converter's flattened HTML).
  case: (s) => (TYPE.microCase === "uppercase" ? String(s ?? "").toUpperCase() : String(s ?? "")),
};

// A table's rows are not paragraphs. `TYPE.bodyLineHeight` is the leading a
// brand sets PROSE in — generic's 1.6 is right for a paragraph somebody reads
// straight through and much too loose for a cell holding "150 ms" — and using
// it for a grid cost generic a whole slide on every reference catalogue: the
// CAMARA table came out on two slides where Telia's 1.2 put it on one, the same
// grid, the same columns, the same reader. So a table gets a leading of its
// own, capped rather than declared: a brand that already sets prose tight keeps
// exactly what it had (Telia stays 1.2), and one that sets prose loose has its
// tables brought in. Read by tableHeight() (the arithmetic), the Table block,
// the exported player and the canvas converter — all four, or a grid measured
// one way and drawn another is a table that overflows its box.
export const TABLE_LEADING = Math.min(TYPE.bodyLineHeight || 1.5, 1.3);

// What the exported deck player receives as its FONTS argument: the theme
// roles plus the resolved micro-typography, so the serialised player never
// has to know which font role or case a brand picked.
export const PLAYER_FONTS = {
  ...FONTS,
  micro: MICRO.font,
  microCase: MICRO.transform,
  bodyLineHeight: TYPE.bodyLineHeight,
  listLineHeight: TYPE.listLineHeight,
  tableLineHeight: TABLE_LEADING,
  // Colours the brand does not allow as text, for the player's own textSafe().
  neverText: NEVER_TEXT,
};

// ── standing slide furniture ────────────────────────────────────────────────
// A corporate template prints marks of its own on every slide: a brand mark,
// and usually a confidentiality marking. The cover gets its own treatment —
// Telia's carries the full Telia Company lockup where an inner page carries
// just the symbol and the marking — so a brand declares the two cases
// separately, as FURNITURE.first and FURNITURE.rest. This resolves them for
// one slide, and returns null where nothing should be drawn: either half may
// be left out, and a brand that declares no furniture draws none anywhere.
//
// It is furniture, not content: it is not in the deck JSON, so it cannot be
// selected, dragged, exported as an element or flagged by the ✓ Review panel,
// and a deck moved between brands picks up the furniture of wherever it is
// opened. Every render path (the stage, the navigator thumbnails, present
// mode, the exported deck, the converter that feeds generated pages and the
// .pptx) asks this one function.
// ── the confidentiality marking a deck carries ──────────────────────────────
// The marking is the one piece of furniture that is not the same on every
// deck: a corporate template prints one word in that placeholder and which
// word depends on the material, not on the brand. So a brand declares the
// words it may print (FURNITURE.markings, keyed by id) and a deck stores which
// one it is (deck.classification). The SHAPES are still furniture — not in the
// deck JSON, not selectable, not exported as elements — and this changes none
// of that: what the deck carries is one short id naming a word the brand
// already declared.
//
// MARKINGS is exported because three surfaces state the list: the Inspector's
// own select, the check that holds the render paths together, and this.
export const MARKINGS = (FURNITURE && FURNITURE.markings) || null;

/** The id an unstamped deck resolves to — whichever marking IS the default. */
export function defaultMarking() {
  const label = FURNITURE && FURNITURE.rest && FURNITURE.rest.label;
  if (!MARKINGS || !label) return "";
  return Object.keys(MARKINGS).find((k) => MARKINGS[k] === label.text) || "";
}

// The label a half of the furniture prints for one classification. Three
// answers and the third is the one that matters:
//
//   nothing stored, or the brand declares no markings → the label as declared
//   an id the brand knows                             → that word
//   an id it does NOT know                            → no label at all
//
// The last is a deck marked on another brand and opened here. Printing the
// declared default there would put "Public" on a deck somebody classified
// internal, which is the one failure a confidentiality marking exists to
// prevent — so it prints nothing, which is visibly missing rather than quietly
// wrong.
function markLabel(label, marking) {
  if (!label || !marking) return label;
  if (!MARKINGS) return label;
  const text = MARKINGS[marking];
  if (!text) return null;
  return text === label.text ? label : { ...label, text };
}

/** FURNITURE with its markings resolved — what a serialised player receives. */
export function markedFurniture(marking = "") {
  if (!FURNITURE || !marking || !MARKINGS) return FURNITURE;
  const half = (f) => {
    if (!f || !f.label) return f;
    const label = markLabel(f.label, marking);
    return label === f.label ? f : { ...f, ...(label ? { label } : {}), ...(label ? {} : { label: null }) };
  };
  return { ...FURNITURE, first: half(FURNITURE.first), rest: half(FURNITURE.rest) };
}

export function slideFurniture(index, backdrop = null, marking = "") {
  if (!FURNITURE) return null;
  let f = index === 0 ? FURNITURE.first : FURNITURE.rest;
  if (f && f.label && marking) {
    const label = markLabel(f.label, marking);
    if (label !== f.label) f = label ? { ...f, label } : { ...f, label: null };
  }
  if (!f || !(f.label || f.logo)) return null;
  // The marking is ink on the slide like any other text, and a brand that sets
  // its content pages light and its statement pages dark needs it to follow.
  // The template states one colour because the template has one ground; the
  // studio can put a slide on either, so the colour is resolved per slide.
  if (!backdrop || !f.label) return f;
  const color = readableOn(f.label.color, backdrop);
  return color === f.label.color ? f : { ...f, label: { ...f.label, color } };
}

// What exported decks and generated pages put in their <style>: the faces the
// build inlined for this brand (fonts.embedInExports), then any extra CSS the
// brand authored by hand.
//
// Fetched on demand rather than imported, because the faces are the largest
// thing this build produces — Telia's seven are 295 kB of base64 — and NOTHING
// in the editor renders with them. They exist so an exported deck and a
// generated page carry the family with them; the editor itself uses the faces
// the build put in its own stylesheet. Importing them here put a quarter of a
// megabyte of base64 into the main bundle, downloaded and parsed on every page
// load, for a file most sessions never produce.
//
// There is no synchronous reader on purpose. Every caller is an export, every
// export is already async, and a sync accessor that answers "" until something
// has warmed it is a font that silently goes missing from one deck in ten.
let _faces = null;
export async function loadExportFontCss() {
  if (_faces === null) {
    // A brand with no faces to embed still has its hand-written CSS, and a
    // chunk that will not load is a deck in the fallback stack rather than no
    // deck at all.
    try { _faces = (await import("../generated/fonts")).embeddedFontCss || ""; }
    catch { _faces = ""; }
  }
  return _faces + brandExportFontCss;
}

// ── colour helpers ──────────────────────────────────────────────────────────

// A palette colour at a given alpha. Accepts #rgb/#rrggbb and rgb()/rgba().
export function tint(color, alpha) {
  const rgb = rgbOf(color);
  return rgb ? `rgba(${rgb},${alpha})` : color;
}

function rgbOf(c) {
  if (typeof c !== "string") return null;
  const s = c.trim();
  let m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
  if (m) {
    let h = m[1];
    if (h.length === 3) h = h.split("").map((x) => x + x).join("");
    return `${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)}`;
  }
  m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(s);
  return m ? `${m[1]},${m[2]},${m[3]}` : null;
}

// ── contrast ────────────────────────────────────────────────────────────────
// Relative luminance and the WCAG contrast ratio, over any colour the studio
// uses — a hex, an rgb()/rgba() string, or an [r,g,b] triple. The ✓ Review
// panel judges a deck's own colours with these and createElement picks an
// inserted element's colours with them, so the editor never hands you something
// its own linter would flag.
export const MIN_CONTRAST = 3; // the ratio Review asks of text on a background

const triple = (c) => {
  if (Array.isArray(c)) return c.length >= 3 ? c : null;
  const rgb = rgbOf(c);
  return rgb ? rgb.split(",").map(Number) : null;
};
const relLum = (rgb) => {
  const f = (v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(rgb[0]) + 0.7152 * f(rgb[1]) + 0.0722 * f(rgb[2]);
};

export function contrastRatio(a, b) {
  const x = triple(a), y = triple(b);
  if (!x || !y) return 21; // not a colour we can judge — never flag it
  const la = relLum(x), lb = relLum(y);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

// The colour a mark should take to stay legible on `backdrop`. A colour that
// already reads is returned untouched — this only rescues the ones that would
// land invisible, so a deck authored for the dark backgrounds the studio ships
// is not repainted. The rescue keeps the original's alpha, which is what makes
// a dimmed tone come back dimmed rather than solid.
export function readableOn(color, backdrop, min = MIN_CONTRAST) {
  if (!color || !backdrop || !triple(color) || !triple(backdrop)) return color;
  if (contrastRatio(color, backdrop) >= min) return color;
  const target = contrastRatio(P.light, backdrop) >= contrastRatio(P.deep, backdrop) ? P.light : P.deep;
  const m = /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*([\d.]+)\s*\)$/i.exec(String(color).trim());
  const alpha = m ? Number(m[1]) : 1;
  return alpha < 1 ? tint(target, alpha) : target;
}

// ── brandCss ────────────────────────────────────────────────────────────────
// The Studio's static stylesheets — editor chrome, the exported deck player,
// generated interactive pages — are authored against the *generic* palette.
// Piping them through brandCss() swaps those literals for the active brand's,
// so a brand declares values only and never a substitution table.
//
// AUTHORED mirrors brand/generic/brand.config.js. Keep the two in step: a
// literal that is not listed here simply passes through unchanged.
const AUTHORED = {
  hex: {
    "#0B1026": P.deep,
    "#161D38": P.dark,
    "#1C2547": P.card,
    "#6C5CE7": P.purple,
    "#E9ECFF": P.light,
    "#E15FD5": P.magenta,
    "#0FA396": P.teal,
    "#0A0F22": CHROME.bg,
    "#171F3A": CHROME.menu,
    "#7D6EF5": CHROME.hover,
    "#171F3C": CHROME.stageTop,
    "#090D1E": CHROME.stageBottom,
    "#141B34": CHROME.diagramTop,
    "#8B7BF0": CHROME.numAccent,
    "#7DD3FC": P.electric,
    "#FFD740": P.gold,
    "#69F0AE": P.green,
    "#FF5252": P.red,
    "#FF8A65": P.orange,
    // The bright accent doubles as the editor's interactive colour, and every
    // literal occurrence of it in src is chrome (focus rings, busy states),
    // so it translates to CHROME.accent rather than to the palette's P.cyan.
    "#00D4FF": CHROME.accent,
  },
  // Bare r,g,b triples, matched only inside rgb()/rgba() so a stray number
  // sequence elsewhere in the CSS can never be rewritten. "153,10,227" is the
  // Telia purple that had leaked into a few generic rules — mapping it too
  // normalises those onto whichever brand is building.
  rgb: {
    "233,236,255": CHROME.lightRgb,
    "108,92,231": CHROME.purpleRgb,
    "153,10,227": CHROME.purpleRgb,
    "22,29,56": CHROME.darkRgb,
    "12,17,36": CHROME.barRgb,
    "7,10,22": CHROME.scrimRgb,
    "6,9,18": CHROME.scrim2Rgb,
    // The editor accent, written at many alphas. "56,224,255" is a slightly
    // brighter hand-mixed variant of the same accent in the converter grid.
    "0,212,255": CHROME.accentRgb,
    "56,224,255": CHROME.accentRgb,
  },
};

const HEX_RE = new RegExp(Object.keys(AUTHORED.hex).join("|"), "gi");
const RGB_RE = new RegExp(`(rgba?\\(\\s*)(${Object.keys(AUTHORED.rgb).map((k) => k.replace(/,/g, "\\s*,\\s*")).join("|")})`, "gi");
const CANON = (s) => s.replace(/\s+/g, "");

export function brandCss(css) {
  return String(css)
    .replace(HEX_RE, (m) => AUTHORED.hex[m.toUpperCase()] || m)
    .replace(RGB_RE, (m, open, triple) => open + (AUTHORED.rgb[CANON(triple)] || triple));
}

// The inline favicon that exported decks and generated pages carry with them,
// ready to drop into an href.
export const FAVICON_DATA_URI = "data:image/svg+xml," + encodeURIComponent(favicon.inline);
